import { useState } from "react";
import { QrCode } from "@phosphor-icons/react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";
import QrScanner from "./QrScanner";

/**
 * VehicleQrField — unified vehicle picker used across every record-entry
 * form (inspection start, fuel log, quote request, ...). Shows the selected
 * vehicle as a read-only display and opens the QR scanner on demand.
 *
 * Props:
 *   value       string  vehicle id currently selected (or "" for none)
 *   vehicles    array   list of vehicles known to the caller — used to
 *                       render plate/make/model without a follow-up fetch
 *   onPick      func    receives the full vehicle object once resolved
 *   required    bool    hidden mirror input keeps native form validation
 *   testid      string  base data-testid — sub-parts append `-btn` etc.
 *   autoOpen    bool    open the scanner immediately on first mount
 */
export default function VehicleQrField({
    value,
    vehicles = [],
    onPick,
    required = false,
    testid = "vehicle-qr-field",
    autoOpen = false,
}) {
    const [open, setOpen] = useState(autoOpen);

    const v = vehicles.find((x) => x.id === value);

    const handleDetect = async (code) => {
        try {
            const { data } = await api.post("/vehicles/resolve-qr", { code });
            toast.success(`車両を選択: ${data.plate_number}`);
            setOpen(false);
            onPick(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "QRコードを認識できません");
        }
    };

    return (
        <>
            <div className="flex items-center gap-2">
                <div
                    className="prc-input flex-1 !opacity-90 !cursor-default select-text"
                    data-testid={`${testid}-readonly`}
                >
                    {v ? (
                        <span className="font-mono">
                            <span className="font-bold text-[var(--prc-ink)]">{v.plate_number}</span>
                            {(v.make || v.model) && (
                                <span className="text-[var(--prc-muted)] ml-2">— {v.make} {v.model}</span>
                            )}
                        </span>
                    ) : (
                        <span className="text-[var(--prc-tertiary)]">QRスキャンで車両を選択してください</span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="prc-btn-ghost inline-flex items-center gap-2 whitespace-nowrap"
                    data-testid={`${testid}-btn`}
                    title="QRコードで車両を選択"
                >
                    <QrCode size={16} />{v ? "再スキャン" : "スキャン"}
                </button>
                {required && (
                    <input
                        type="text"
                        required
                        value={value || ""}
                        readOnly
                        onChange={() => {}}
                        aria-hidden="true"
                        tabIndex={-1}
                        style={{ position: "absolute", opacity: 0, pointerEvents: "none", height: 0, width: 0 }}
                    />
                )}
            </div>
            <QrScanner open={open} onClose={() => setOpen(false)} onDetect={handleDetect} />
        </>
    );
}
